import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ChevronRight, Search, Users, TrendingUp } from 'lucide-react';
import { clientsApi, ordersApi } from '../api/cms';
import { defaultClients, defaultOrders } from '../api/defaults';
import type { Client, Order } from '../types';
import { useLang } from '../context/LangContext';

const STATUS_CFG = {
  active:   { ro: 'Activ',    en: 'Active',   color: 'bg-green-500/20 text-green-400' },
  inactive: { ro: 'Inactiv',  en: 'Inactive', color: 'bg-red-500/20 text-red-400' },
  prospect: { ro: 'Prospect', en: 'Prospect', color: 'bg-yellow-500/20 text-yellow-400' },
};

export default function ClientsPage() {
  const { lang, t } = useLang();
  const [clients, setClients] = useState<Client[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      clientsApi.list().catch((_e: unknown) => defaultClients),
      ordersApi.list().catch((_e: unknown) => defaultOrders),
    ]).then(([c, o]) => { setClients(c); setOrders(o); setLoading(false); });
  }, []);

  const getClientOrderCount = (clientId: string) => orders.filter(o => o.clientId === clientId).length;
  const getClientRevenue = (clientId: string) => orders.filter(o => o.clientId === clientId && o.status === 'completed').reduce((s, o) => s + o.total, 0);

  const filtered = clients.filter(c =>
    (c.name.toLowerCase().includes(filter.toLowerCase()) ||
     c.company.toLowerCase().includes(filter.toLowerCase()) ||
     c.email.toLowerCase().includes(filter.toLowerCase()) || !filter) &&
    (c.status === statusFilter || !statusFilter)
  );

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    prospects: clients.filter(c => c.status === 'prospect').length,
    revenue: orders.filter(o => o.status === 'completed').reduce((s, o) => s + o.total, 0),
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('Clienți', 'Clients')}</h1>
          <p className="text-white/40 text-sm mt-1">{t('Baza de clienți și serviciile active', 'Client base and active services')}</p>
        </div>
        <Link to="/clients/new"
          className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={16} /> {t('Client nou', 'New Client')}
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: t('Total clienți', 'Total clients'), value: stats.total, icon: Users, color: 'text-blue-400' },
          { label: t('Clienți activi', 'Active clients'), value: stats.active, icon: TrendingUp, color: 'text-green-400' },
          { label: t('Prospecți', 'Prospects'), value: stats.prospects, icon: Users, color: 'text-yellow-400' },
          { label: t('Venituri totale', 'Total revenue'), value: `${stats.revenue.toLocaleString()} EUR`, icon: TrendingUp, color: 'text-[#4ab8ff]' },
        ].map(s => (
          <div key={s.label} className="bg-[#07090f] border border-white/10 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-white/40 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={filter} onChange={e => setFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors"
            placeholder={t('Caută client, companie...', 'Search client, company...')} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors">
          <option value="">{t('Toate', 'All')}</option>
          <option value="active">{t('Activi', 'Active')}</option>
          <option value="inactive">{t('Inactivi', 'Inactive')}</option>
          <option value="prospect">{t('Prospecți', 'Prospects')}</option>
        </select>
      </div>

      {/* Client cards grid */}
      {loading ? (
        <div className="p-8 text-center text-white/30 text-sm">{t('Se încarcă...', 'Loading...')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(c => {
            const cfg = STATUS_CFG[c.status];
            const revenue = getClientRevenue(c.id);
            const orderCount = getClientOrderCount(c.id);
            return (
              <Link key={c.id} to={`/clients/${c.id}`}
                className="bg-[#07090f] border border-white/10 rounded-xl p-5 hover:border-[#295CA8]/40 transition-all group">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white group-hover:text-[#4ab8ff] transition-colors truncate">{c.company || c.name}</div>
                    <div className="text-xs text-white/40 mt-0.5 truncate">{c.name}</div>
                  </div>
                  <span className={`ml-2 flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                    {lang === 'ro' ? cfg.ro : cfg.en}
                  </span>
                </div>

                <div className="space-y-1.5 mb-4">
                  <div className="text-xs text-white/40 truncate">✉ {c.email}</div>
                  <div className="text-xs text-white/40">📞 {c.phone}</div>
                </div>

                {/* Service pills */}
                {c.services.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {c.services.slice(0, 4).map((s, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded bg-[#295CA8]/20 text-[#4ab8ff] border border-[#295CA8]/30">
                        {s.productName}
                      </span>
                    ))}
                    {c.services.length > 4 && (
                      <span className="text-xs px-2 py-0.5 rounded bg-white/5 text-white/30">+{c.services.length - 4}</span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <div className="flex gap-4">
                    <div><div className="text-xs text-white/30">{t('Servicii', 'Services')}</div><div className="text-sm font-semibold text-white">{c.services.filter(s => s.status === 'active').length}</div></div>
                    <div><div className="text-xs text-white/30">{t('Comenzi', 'Orders')}</div><div className="text-sm font-semibold text-white">{orderCount}</div></div>
                    {revenue > 0 && <div><div className="text-xs text-white/30">Revenue</div><div className="text-sm font-semibold text-green-400">{revenue.toLocaleString()} €</div></div>}
                  </div>
                  <ChevronRight size={16} className="text-white/20 group-hover:text-[#4ab8ff] transition-colors" />
                </div>
              </Link>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-3 p-8 text-center text-white/30 text-sm">{t('Niciun client găsit.', 'No clients found.')}</div>
          )}
        </div>
      )}
    </div>
  );
}
